import { Button, Input, Select, Switch } from "@arco-design/web-react";
import { RotateCcw, Search } from "lucide-react";

import { getCityOptions, getDistrictOptions, getProvinceOptions } from "../../../shared/region";
import {
  resourcePointGeoStatusFilterOptions,
  resourcePointSubTypeOptions,
  resourcePointTypeOptions,
  resourcePointTypeWithSubType,
  scopeHidesTypeFilters,
  type ResourcePointListScope,
} from "../config";
import type { ResourcePointListFilters } from "../types";
import "../../../shared/ui/crmListPageLayout.css";
import "./resourcePointManagement.css";

type ResourcePointFilterPanelProps = {
  filters: ResourcePointListFilters;
  fetching: boolean;
  scope?: ResourcePointListScope;
  onChange: (filters: ResourcePointListFilters) => void;
  onReset: () => void;
  onSearch: () => void;
};

/** 考察点 SPU 列表筛选带。 */
export function ResourcePointFilterPanel({
  filters,
  fetching,
  scope = "all",
  onChange,
  onReset,
  onSearch,
}: ResourcePointFilterPanelProps) {
  const lockTypeFilters = scopeHidesTypeFilters(scope);
  const showSubType = !lockTypeFilters
    && filters.pointType === resourcePointTypeWithSubType;
  const cityOptions = filters.province ? getCityOptions(filters.province) : [];
  const districtOptions = filters.province && filters.city
    ? getDistrictOptions(filters.province, filters.city)
    : [];

  return (
    <section
      className="crm-list-filter-panel resource-point-filter"
      role="search"
      aria-label="考察点筛选"
      data-layout="two-tier-responsive"
    >
      <div className="crm-list-filter-fields resource-point-filter__fields">
        <div
          className="crm-list-filter-primary"
          data-testid="resource-point-region-filter"
        >
          <label className="crm-list-field crm-list-field--lg resource-point-field">
            <span>关键词</span>
            <Input
              allowClear
              aria-label="关键词"
              placeholder="名称"
              value={filters.keyword ?? ""}
              onChange={(keyword) => onChange({ ...filters, keyword })}
              onPressEnter={onSearch}
            />
          </label>
          {lockTypeFilters ? null : (
            <div className="crm-list-field crm-list-field--md resource-point-field">
              <span>考察点类型</span>
              <Select
                allowClear
                showSearch
                aria-label="考察点类型筛选"
                data-testid="resource-point-type-filter"
                getPopupContainer={popupContainer}
                placeholder="全部类型"
                options={[...resourcePointTypeOptions]}
                value={filters.pointType}
                onChange={(pointType) => onChange({
                  ...filters,
                  pointType,
                  pointSubType: pointType === resourcePointTypeWithSubType
                    ? filters.pointSubType
                    : undefined,
                })}
              />
            </div>
          )}
          {showSubType ? (
            <div className="crm-list-field crm-list-field--md resource-point-field">
              <span>二级类型</span>
              <Select
                allowClear
                aria-label="考察点二级类型筛选"
                data-testid="resource-point-subtype-filter"
                getPopupContainer={popupContainer}
                placeholder="全部二级类型"
                options={[...resourcePointSubTypeOptions]}
                value={filters.pointSubType}
                onChange={(pointSubType) => onChange({ ...filters, pointSubType })}
              />
            </div>
          ) : null}
          <div className="crm-list-field crm-list-field--md resource-point-field">
            <span>GEO状态</span>
            <Select
              allowClear
              aria-label="GEO状态筛选"
              data-testid="resource-point-geo-status-filter"
              getPopupContainer={popupContainer}
              placeholder="全部GEO状态"
              options={[...resourcePointGeoStatusFilterOptions]}
              value={filters.geoStatus}
              onChange={(geoStatus) => onChange({ ...filters, geoStatus })}
            />
          </div>
          <div className="crm-list-field crm-list-field--md resource-point-field">
            <span>省份</span>
            <Select
              allowClear
              showSearch
              aria-label="省份筛选"
              data-testid="resource-point-province-filter"
              getPopupContainer={popupContainer}
              placeholder="全部省份"
              options={getProvinceOptions().map((value) => ({ label: value, value }))}
              value={filters.province}
              onChange={(province) => onChange({
                ...filters,
                province,
                city: undefined,
                district: undefined,
              })}
            />
          </div>
          <div className="crm-list-field crm-list-field--md resource-point-field">
            <span>城市</span>
            <Select
              allowClear
              showSearch
              aria-label="城市筛选"
              data-testid="resource-point-city-filter"
              disabled={!filters.province}
              getPopupContainer={popupContainer}
              placeholder="全部城市"
              options={cityOptions.map((value) => ({ label: value, value }))}
              value={filters.city}
              onChange={(city) => onChange({ ...filters, city, district: undefined })}
            />
          </div>
          <div className="crm-list-field crm-list-field--md resource-point-field">
            <span>区县</span>
            <Select
              allowClear
              showSearch
              aria-label="区县筛选"
              data-testid="resource-point-district-filter"
              disabled={!filters.city}
              getPopupContainer={popupContainer}
              placeholder="全部区县"
              options={districtOptions.map((value) => ({ label: value, value }))}
              value={filters.district}
              onChange={(district) => onChange({ ...filters, district })}
            />
          </div>
          <label className="crm-list-field resource-point-filter__switch">
            <span>仅看停用</span>
            <Switch
              aria-label="仅看停用"
              checked={filters.isDisabled === true}
              onChange={(checked) => onChange({ ...filters, isDisabled: checked ? true : undefined })}
            />
          </label>
        </div>
      </div>
      <div className="crm-list-filter-actions resource-point-filter__actions">
        <Button aria-label="重置筛选" icon={<RotateCcw size={16} />} onClick={onReset}>
          重置
        </Button>
        <Button
          type="primary"
          icon={<Search size={16} />}
          loading={fetching}
          onClick={onSearch}
        >
          查询
        </Button>
      </div>
    </section>
  );
}

/** 将筛选下拉挂到 body，避免搜索后弹层被表格区域挡住。 */
function popupContainer() {
  return document.body;
}
